import { useMutation, useQueryClient } from "react-query";
import { clearStoredAuth } from "../utils";
import { useNavigate } from "react-router-dom";
import { useEffect, useState, ChangeEvent, SyntheticEvent } from "react";
import { SelectChangeEvent } from "@mui/material";
import { API_URL } from "../config";
import { validateEmail, getAssignableRoles, getNormalizedUserRole } from "../utils";
import useAuthSnapshot from "./useAuthSnapshot";

type UserIosSettingsVariant = "default" | "test";

const postUser = async (user: User) => {
  const newUser = { ...user };

  if (typeof newUser.password === "string" && newUser.password.trim().length === 0) {
    newUser.password = null;
  }
  if (typeof newUser.pin === "string" && newUser.pin.trim().length === 0) {
    newUser.pin = null;
  }
  if (typeof newUser.tlc === "string" && newUser.tlc.trim().length === 0) {
    newUser.tlc = null;
  }

  if (newUser.role === "refuel_eu_reader") {
    newUser.type = 1;
    newUser.aircraft_assignments = [];
  }

  if (newUser.type == 0) {
    newUser.password = null;
  } else if (newUser.type == 1) {
    newUser.pin = null;
    newUser.tlc = null;
    newUser.can_see_all_flights = false;
    newUser.ios_settings_variant = "default";
  }

  if (newUser.role === "acmi_client") {
    newUser.type = 1;
    newUser.pin = null;
    newUser.tlc = null;
    newUser.can_see_all_flights = false;
    newUser.ios_settings_variant = "default";
    newUser.acmi_callsign_prefix = (newUser.acmi_callsign_prefix || "").trim().toUpperCase();
  } else {
    newUser.acmi_callsign_prefix = null;
  }

  const res = await fetch(`${API_URL}/users`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(newUser),
    credentials: "include",
  });

  if (!res.ok) {
    throw { message: res.statusText, code: res.status };
  }

  return res.json();
};

type AddUserOperatorValue = {
  id: number | null;
  label: string;
  booked_options: string[];
};

type UseAddUserOptions = {
  defaultOperator?: Partial<AddUserOperatorValue> | null;
  defaultRole?: UserRole;
  defaultType?: number;
  onSuccess?: (message: string) => void;
  resetOnSuccess?: boolean;
};

const EMPTY_OPERATOR: AddUserOperatorValue = {
  id: null,
  label: "",
  booked_options: [],
};

const normalizeAddUserOperator = (
  value?: Partial<AddUserOperatorValue> | null,
): AddUserOperatorValue => {
  const nextId = typeof value?.id === "number" ? value.id : Number(value?.id ?? NaN);

  return {
    id: Number.isFinite(nextId) ? nextId : null,
    label: value?.label ?? "",
    booked_options: value?.booked_options ?? [],
  };
};

export const useAddUser = (options: UseAddUserOptions = {}) => {
  const { userData: storedUser } = useAuthSnapshot();
  const currentUserRole = getNormalizedUserRole(storedUser?.role, storedUser?.is_admin);
  const availableRoles = getAssignableRoles(currentUserRole);
  const defaultRole: UserRole =
    options.defaultRole && availableRoles.includes(options.defaultRole)
      ? options.defaultRole
      : availableRoles.includes("user")
      ? "user"
      : availableRoles[0];
  const defaultType = defaultRole === "refuel_eu_reader" ? 1 : Number.isFinite(options.defaultType) ? Number(options.defaultType) : 0;
  const defaultOperator = normalizeAddUserOperator(options.defaultOperator);
  const defaultOperatorBookedOptionsKey = defaultOperator.booked_options.join("|");

  const [email, setEmail] = useState("");
  const [type, setType] = useState(defaultType);
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [operator, setOperator] = useState<AddUserOperatorValue>(defaultOperator);
  const [tlc, setTlc] = useState("");
  const [canSeeAllFlights, setCanSeeAllFlights] = useState(false);
  const [iosSettingsVariant, setIosSettingsVariant] =
    useState<UserIosSettingsVariant>("default");
  const [acmiCallsignPrefix, setAcmiCallsignPrefix] = useState("");
  const [role, setRole] = useState<UserRole>(defaultRole);
  const [aircraftAssignments, setAircraftAssignments] = useState<UserAircraftAssignment[]>([]);
  const [error, setError] = useState("");
  const [message, setMesssage] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    if (!defaultOperator.id) {
      return;
    }

    setOperator(defaultOperator);
  }, [defaultOperator.id, defaultOperator.label, defaultOperatorBookedOptionsKey]);

  const successMessage = `User ${email} has been added!`;
  const sendResetPin = (nextMessage: string) => {
    fetch(`${API_URL}/users/reset/pin?tlc=${tlc}&operator=${operator?.label}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });
    return `${nextMessage} Reset pin email has been sent.`;
  };
  const sendResetPassword = (nextMessage: string) => {
    fetch(`${API_URL}/users/reset/password?email=${email}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });
    return `${nextMessage} Reset password email has been sent.`;
  };
  const sendResetPinAndPassword = (nextMessage: string) => {
    fetch(`${API_URL}/users/reset/password?email=${email}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });
    fetch(`${API_URL}/users/reset/pin?tlc=${tlc}&operator=${operator?.label}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });
    return `${nextMessage} Reset pin and password emails have been sent.`;
  };

  const resetForm = () => {
    setEmail("");
    setType(defaultType);
    setPassword("");
    setPin("");
    setOperator(defaultOperator.id ? defaultOperator : EMPTY_OPERATOR);
    setTlc("");
    setCanSeeAllFlights(false);
    setIosSettingsVariant("default");
    setAcmiCallsignPrefix("");
    setRole(defaultRole);
    setAircraftAssignments([]);
    setError("");
    setMesssage("");
    setIsModalOpen(false);
  };

  const mutation = useMutation(postUser, {
    onSuccess: () => {
      setIsModalOpen(false);
      queryClient.invalidateQueries('users');
      setError("");
      let nextMessage = successMessage;

      switch (type) {
        case 0: // User with only smartEFB access
          nextMessage = pin ? successMessage : sendResetPin(successMessage);
          break;
        case 1: // User with only FlightViewer access
          nextMessage = password ? successMessage : sendResetPassword(successMessage);
          break;
        case 2: // User with both accesses
          if (!password && !pin) {
            nextMessage = sendResetPinAndPassword(successMessage);
          } else if (!password) {
            nextMessage = sendResetPassword(successMessage);
          } else if (!pin) {
            nextMessage = sendResetPin(successMessage);
          } else {
            nextMessage = successMessage;
          }
          break;
        default:
          nextMessage = successMessage;
      }

      if (options.resetOnSuccess) {
        resetForm();
      } else {
        setMesssage(nextMessage);
        setRole(defaultRole);
      }

      options.onSuccess?.(nextMessage);
    },
    onError: (err: HttpError) => {
      setIsModalOpen(false);
      if (err.code === 401) {
        clearStoredAuth();
        navigate('/');
      }
      setError("You can't add this user");
      setMesssage("");
    },
  });

  const onEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setError("");
    setMesssage("");
  };

  const onOperatorChange = (
    _: SyntheticEvent<Element, Event> | null,
    value: { id: any; label: any; booked_options?: string[] } | null,
  ) => {
    const nextId = typeof value?.id === "number" ? value?.id : Number(value?.id ?? NaN);
    setOperator({
      id: Number.isFinite(nextId) ? nextId : null,
      label: value?.label ?? "",
      booked_options: value?.booked_options ?? [],
    });
    setError("");
    setMesssage("");
  };

  const onTlcChange = (e: ChangeEvent<HTMLInputElement>) => {
    setTlc(e.target.value);
    setError("");
    setMesssage("");
  };

  const onTypeChange = (e: SelectChangeEvent<number>) => {
    const nextType = Number(e.target.value);
    setType(nextType);
    if (nextType === 1) {
      setCanSeeAllFlights(false);
      setIosSettingsVariant("default");
    }
    setError("");
    setMesssage("");
  };

  const onPasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    setError("");
    setMesssage("");
  };

  const onPinChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPin(e.target.value);
    setError("");
    setMesssage("");
  };

  const onRoleChange = (e: SelectChangeEvent<string>) => {
    const nextRole = e.target.value as UserRole;
    setRole(nextRole);
    if (nextRole === "refuel_eu_reader") setAircraftAssignments([]);
    if (nextRole === "acmi_client" || nextRole === "refuel_eu_reader") {
      setType(1);
      setTlc("");
      setPin("");
      setCanSeeAllFlights(false);
      setIosSettingsVariant("default");
    }
    setError("");
    setMesssage("");
  };

  const onAcmiCallsignPrefixChange = (e: ChangeEvent<HTMLInputElement>) => {
    setAcmiCallsignPrefix(e.target.value.toUpperCase());
    setError("");
    setMesssage("");
  };

  const onCanSeeAllFlightsChange = (e: ChangeEvent<HTMLInputElement>) => {
    setCanSeeAllFlights(e.target.checked);
    setError("");
    setMesssage("");
  };

  const onIosSettingsVariantChange = (e: SelectChangeEvent<string>) => {
    setIosSettingsVariant((e.target.value as UserIosSettingsVariant) || "default");
    setError("");
    setMesssage("");
  };

  const handleSubmit = () => {
    mutation.mutate({
      email,
      aircraft_assignments: aircraftAssignments,
      password,
      is_admin: role === "super_admin",
      operator_ref: operator.id || 0,
      tlc,
      pin,
      type,
      role,
      can_see_all_flights: canSeeAllFlights,
      ios_settings_variant: type === 1 ? "default" : iosSettingsVariant,
      acmi_callsign_prefix: role === "acmi_client" ? acmiCallsignPrefix : null,
    });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleConfirmAddUser = (e: SyntheticEvent) => {
    e.preventDefault();
    setError("");
    setMesssage("");
    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }
    if ((type == 0 || type == 2) && (tlc.length === 0 || tlc.length > 5)) {
      setError("Please enter a valid TLC");
      return;
    }
    if (!!password && password.length < 4) {
      setError("Please enter a valid password");
      return;
    }
    if (!!pin && pin.length < 4) {
      setError("Please enter a valid pin");
      return;
    }
    if (role === "acmi_client" && !acmiCallsignPrefix.trim()) {
      setError("Please enter a callsign prefix");
      return;
    }
    if (!Number(operator.id)) {
      setError("Please select an operator");
      return;
    }
    setIsModalOpen(true);
  };

  return {
    email,
    onEmailChange,
    type,
    onTypeChange,
    password,
    onPasswordChange,
    tlc,
    pin,
    onPinChange,
    role,
    onRoleChange,
    acmiCallsignPrefix,
    onAcmiCallsignPrefixChange,
    canSeeAllFlights,
    onCanSeeAllFlightsChange,
    iosSettingsVariant,
    onIosSettingsVariantChange,
    availableRoles,
    operator,
    onOperatorChange,
    aircraftAssignments,
    onAircraftAssignmentsChange: setAircraftAssignments,
    onTlcChange,
    handleSubmit,
    handleCloseModal,
    handleConfirmAddUser,
    isModalOpen,
    status: mutation.status,
    error,
    message,
    resetForm,
  };
};

const deleteUser = async (id: string) => {
  const res = await fetch(`${API_URL}/users/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });

  if (!res.ok) {
    throw new Error(`${res.status}`);
  }

  return res.json();
};

export const useDeleteUser = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation(deleteUser, {
    onSuccess: () => {
      queryClient.invalidateQueries('users');
    },
    onError: (error: Error) => {
      if (error.message === '401') {
        clearStoredAuth();
        navigate('/');
      }
    },
  });
};

const postResetPassword = async ({ email }: User) => {
  const res = await fetch(`${API_URL}/users/reset/password?email=${email}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
  });

  if (!res.ok) {
    throw { message: res.statusText, code: res.status };
  }

  return res.json();
};

export const useResetPassword = () => {
  const navigate = useNavigate();

  return useMutation(postResetPassword, {
    onSuccess: () => {
    },
    onError: (error: Error) => {
      if (error.message === '401') {
        clearStoredAuth();
        navigate('/');
      }
    },
  });
};

const postResetPin = async ({ tlc, operator }: User) => {
  const res = await fetch(`${API_URL}/users/reset/pin?tlc=${tlc}&operator=${operator}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
  });

  if (!res.ok) {
    throw { message: res.statusText, code: res.status };
  }

  return res.json();
};

export const useResetPin = () => {
  const navigate = useNavigate();

  return useMutation(postResetPin, {
    onSuccess: () => {
    },
    onError: (error: Error) => {
      if (error.message === '401') {
        clearStoredAuth();
        navigate('/');
      }
    },
  });
};

const postReactivateUserLogin = async ({ id }: User) => {
  const res = await fetch(`${API_URL}/users/${id}/reactivate-login`, {
    method: "POST",
    credentials: "include",
  });

  if (!res.ok) {
    throw new Error(`${res.status}`);
  }

  return res.json();
};

export const useReactivateUserLogin = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation(postReactivateUserLogin, {
    onSuccess: () => {
      queryClient.invalidateQueries("users");
    },
    onError: (error: Error) => {
      if (error.message === "401") {
        clearStoredAuth();
        navigate("/");
      }
    },
  });
};

const putUser = async ({
  id,
  email,
  is_admin,
  operator_ref,
  type,
  tlc,
  pin,
  password,
  role,
  can_see_all_flights,
  ios_settings_variant,
  acmi_callsign_prefix,
  aircraft_assignments,
}: User) => {
  const user: User = {
    id,
    email,
    is_admin,
    operator_ref,
    tlc,
    pin: pin ? pin : undefined,
    password: password ? password : undefined,
    role,
    can_see_all_flights: false,
    ios_settings_variant: ios_settings_variant || "default",
    acmi_callsign_prefix: role === "acmi_client"
      ? (acmi_callsign_prefix || "").trim().toUpperCase()
      : null,
    aircraft_assignments: Array.isArray(aircraft_assignments) ? aircraft_assignments : [],
  };
  if (type == 0 && role !== "refuel_eu_reader") {
    user.password = null;
  } else if (type == 1 || role === "refuel_eu_reader") {
    user.pin = null;
    user.tlc = null;
    user.ios_settings_variant = "default";
  }
  if (role === "acmi_client") {
    user.pin = null;
    user.tlc = null;
    user.ios_settings_variant = "default";
  }
  if (role === "refuel_eu_reader") user.aircraft_assignments = [];
  user.can_see_all_flights = type === 1 || role === "refuel_eu_reader" ? false : Boolean(can_see_all_flights);
  const res = await fetch(`${API_URL}/users/${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(user),
    credentials: "include",
  });

  if (!res.ok) {
    throw { message: res.statusText, code: res.status };
  }

  return res.json();
};

export const usePutUser = () => {
  const { userData: storedUser } = useAuthSnapshot();
  const currentUserRole = getNormalizedUserRole(storedUser?.role, storedUser?.is_admin);
  const availableRoles = getAssignableRoles(currentUserRole);
  const defaultRole: UserRole = availableRoles.includes("user") ? "user" : availableRoles[0];
  const defaultOperator = normalizeAddUserOperator({
    id: storedUser?.operator_ref ?? null,
    label: storedUser?.operator_name ?? "",
    booked_options: [],
  });

  const [userId, setUserId] = useState("");
  const [email, setEmail] = useState("");
  const [type, setType] = useState(0);
  const [dbUserType, setDbUserType] = useState(0);
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [tlc, setTlc] = useState("");
  const [canSeeAllFlights, setCanSeeAllFlights] = useState(false);
  const [iosSettingsVariant, setIosSettingsVariant] =
    useState<UserIosSettingsVariant>("default");
  const [acmiCallsignPrefix, setAcmiCallsignPrefix] = useState("");
  const [role, setRole] = useState<UserRole>(defaultRole);
  const [operator, setOperator] = useState<AddUserOperatorValue>(defaultOperator);
  const [aircraftAssignments, setAircraftAssignments] = useState<UserAircraftAssignment[]>([]);
  const [error, setError] = useState("");
  const [message, setMesssage] = useState("");

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const mutation = useMutation(putUser, {
    onSuccess: () => {
      queryClient.invalidateQueries('users');
      setError("");
      setDbUserType(type);
      setPassword("");
      setPin("");
      setMesssage(`User ${email} has been updated!`);
    },
    onError: (err: HttpError) => {
      if (err.code === 401) {
        clearStoredAuth();
        navigate('/');
      }

      setError("You can't update this user");
      setMesssage("");
    },
  });

  const setUser = (user: User) => {
    setType(user.type || 0)
    setDbUserType(user.type || 0)
    setPassword("");
    setPin("");
    setUserId(user.id || "");
    setEmail(user.email);
    setTlc(user.tlc || "");
    setCanSeeAllFlights(Boolean(user.can_see_all_flights));
    setAcmiCallsignPrefix(user.acmi_callsign_prefix || "");
    setAircraftAssignments(
      Array.isArray(user.aircraft_assignments) ? user.aircraft_assignments : [],
    );
    setIosSettingsVariant(
      user.ios_settings_variant === "test" && (user.type === 0 || user.type === 2)
        ? "test"
        : "default",
    );
    setOperator(
      normalizeAddUserOperator({
        id: user.operator_ref ?? null,
        label: user.operator ?? "",
        booked_options: [],
      }),
    );
    const normalizedRole = getNormalizedUserRole(user.role, user.is_admin);
    setRole(availableRoles.includes(normalizedRole) ? normalizedRole : defaultRole);
    setError("");
    setMesssage("");
  }

  const onEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setError("");
    setMesssage("");
  };

  const onTlcChange = (e: ChangeEvent<HTMLInputElement>) => {
    setTlc(e.target.value);
    setError("");
    setMesssage("");
  };

  const onTypeChange = (e: SelectChangeEvent<number>) => {
    const nextType = Number(e.target.value);
    setType(nextType);
    if (nextType === 1) {
      setCanSeeAllFlights(false);
      setIosSettingsVariant("default");
    }
    setError("");
    setMesssage("");
  };

  const onPasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    setError("");
    setMesssage("");
  };

  const onPinChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPin(e.target.value);
    setError("");
    setMesssage("");
  };

  const onRoleChange = (e: SelectChangeEvent<string>) => {
    const nextRole = e.target.value as UserRole;
    setRole(nextRole);
    if (nextRole === "refuel_eu_reader") setAircraftAssignments([]);
    if (nextRole === "acmi_client" || nextRole === "refuel_eu_reader") {
      setType(1);
      setTlc("");
      setPin("");
      setCanSeeAllFlights(false);
      setIosSettingsVariant("default");
    }
    setMesssage("");
  };

  const onAcmiCallsignPrefixChange = (e: ChangeEvent<HTMLInputElement>) => {
    setAcmiCallsignPrefix(e.target.value.toUpperCase());
    setError("");
    setMesssage("");
  };

  const onOperatorChange = (
    _: SyntheticEvent<Element, Event> | null,
    value: { id: any; label: any; booked_options?: string[] } | null,
  ) => {
    const nextId = typeof value?.id === "number" ? value?.id : Number(value?.id ?? NaN);
    setOperator({
      id: Number.isFinite(nextId) ? nextId : null,
      label: value?.label ?? "",
      booked_options: value?.booked_options ?? [],
    });
    setError("");
    setMesssage("");
  };

  const onCanSeeAllFlightsChange = (e: ChangeEvent<HTMLInputElement>) => {
    setCanSeeAllFlights(e.target.checked);
    setError("");
    setMesssage("");
  };

  const onIosSettingsVariantChange = (e: SelectChangeEvent<string>) => {
    setIosSettingsVariant((e.target.value as UserIosSettingsVariant) || "default");
    setError("");
    setMesssage("");
  };

  const onSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    setError("");
    setMesssage("");
    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }
    if (!Number(operator.id)) {
      setError("Please select an operator");
      return;
    }
    if ((type == 0 || type == 2) && (tlc.length === 0 || tlc.length > 5)) {
      setError("Please enter a valid TLC");
      return;
    }
    if ((!password && dbUserType == 0 && type !== dbUserType)) {
      setError("Please enter a valid password");
      return;
    }
    if ((!!password && password.length < 4)) {
      setError("Please enter a valid password");
      return;
    }
    if ((!pin && dbUserType == 1 && type !== dbUserType)) {
      setError("Please enter a valid PIN");
      return;
    }
    if (!!pin && pin.length < 4) {
      setError("Please enter a valid PIN");
      return;
    }
    if (role === "acmi_client" && !acmiCallsignPrefix.trim()) {
      setError("Please enter a callsign prefix");
      return;
    }
    mutation.mutate({
      id: userId,
      email,
      is_admin: role === "super_admin",
      operator_ref: operator.id || 0,
      tlc: tlc || null,
      pin,
      password,
      type,
      role,
      can_see_all_flights: canSeeAllFlights,
      ios_settings_variant: type === 1 ? "default" : iosSettingsVariant,
      acmi_callsign_prefix: role === "acmi_client" ? acmiCallsignPrefix : null,
      aircraft_assignments: aircraftAssignments,
    });
  }

  return {
    setUser,
    email,
    onEmailChange,
    tlc,
    onTlcChange,
    type,
    dbUserType,
    onTypeChange,
    password,
    onPasswordChange,
    pin,
    onPinChange,
    role,
    onRoleChange,
    acmiCallsignPrefix,
    onAcmiCallsignPrefixChange,
    canSeeAllFlights,
    onCanSeeAllFlightsChange,
    iosSettingsVariant,
    onIosSettingsVariantChange,
    availableRoles,
    operator,
    onOperatorChange,
    aircraftAssignments,
    onAircraftAssignmentsChange: setAircraftAssignments,
    onSubmit,
    status: mutation.status,
    error,
    message,
  };
};
